import styled from '@emotion/styled';

import Link from 'next/link';

import ProductCardImage from '../../../shared/components/product/card/ProductCardImage';
import ProductQuantityChip from './special-deals/ProductQuantityChip';
import { ProductStatus } from '../../../shared/interfaces';
import ProductCardSticker from '../../../shared/components/product/card/ProductCardSticker';
import ProductCardFunction from '../../../shared/components/product/card/ProductCardFunction';
import COLOR from '../../../shared/constant/colorset';
import ProductCardPrice from '../../../shared/components/product/card/ProductCardPrice';
import { ProductMainSelectData } from '../../../shared/interfaces/Product';
import ReviewBubble from '../../../shared/icons/ReviewBubble';
import { NOT_PURCHASABLE_STATUS } from '../../../shared/constant/productStatusCode';

const ProductWrap = styled.a`
  display: block;
`;

const ProductInfo = styled.div`
  padding: 8px 0;
`;

const ContentRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SoldOutLayer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.4);
  color: ${COLOR.kurlyWhite};
  z-index: 1;
`;

const ReviewCount = styled.div`
  display: flex;
  color: ${COLOR.kurlyGray450};
`;

const ReviewIcon = styled.div();

const ReviewNumber = styled.span`
  padding-left: 3px;
  font-weight: 400;
`;

interface Props {
  index: number;
  productNo: number;
  imageUrl: string;
  name: string;
  price: number;
  discount: {
    price: number | null;
    rate: number;
  };
  sticker?: {
    backgroundColor: string;
    opacity: number;
    contents: {
      text: string;
      weight: string;
    }[];
  };
  status: ProductStatus;
  isGroupProduct: boolean;
  canRestockNotify: boolean;
  shortDescription?: string;
  href: string;
  isBuyNow: boolean;
  deliveryTypeNames: string[];
  reviewCount: string;
  quantity?: number;
  className?: string;
  as?: string;
  isMultiplePrice?: boolean;
  isReviewCount?: boolean;
  handleSelectProduct(selectProduct: ProductMainSelectData): void;
  onChangeSoldOutStatus?(): void;
}

export default function MainProductCard({
  index,
  productNo,
  imageUrl,
  price,
  discount,
  name,
  sticker,
  quantity,
  status,
  isGroupProduct,
  canRestockNotify,
  className,
  shortDescription,
  href,
  as,
  isMultiplePrice,
  isBuyNow,
  deliveryTypeNames,
  reviewCount,
  isReviewCount = true,
  handleSelectProduct,
  onChangeSoldOutStatus,
}: Props) {
  const { code, message } = status;
  const isSoldOut = NOT_PURCHASABLE_STATUS.includes(code);
  const handleSelectShortcut = () => {
    handleSelectProduct({ type: 'shortcut', index, productNo });
  };

  return (
    <Link href={href} as={as} passHref prefetch={false}>
      <ProductWrap
        href={href}
        className={className}
        onClick={() => handleSelectProduct({ type: 'content', index, productNo })}
      >
        <ProductCardImage className="image-container" imageUrl={imageUrl}>
          {sticker && (
            <ProductCardSticker
              className="product-sticker"
              backgroundColor={sticker.backgroundColor}
              opacity={sticker.opacity}
              contents={sticker.contents}
            />
          )}
          <ProductCardFunction
            className="product-function"
            contentProductNo={productNo}
            code={code}
            isGroupProduct={isGroupProduct}
            canRestockNotify={canRestockNotify}
            deliveryTypeNames={deliveryTypeNames}
            isBuyNow={isBuyNow}
            selectMainShortcut={handleSelectShortcut}
            onChangeSoldOutStatus={onChangeSoldOutStatus}
          />
          {isSoldOut && (
            <SoldOutLayer className="sold-out-layer">
              <strong>{message?.title || 'Coming Soon'}</strong>
              <span>{message?.content}</span>
            </SoldOutLayer>
          )}
        </ProductCardImage>
        <ProductInfo className="product-info">
          {shortDescription && <div className="product-description">{shortDescription}</div>}
          <h3 className="product-name">{name}</h3>
          <ContentRow className="content-row">
            <ProductCardPrice
              className="product-price"
              price={price}
              discount={discount}
              isMultiplePrice={isMultiplePrice}
            />
            <ProductQuantityChip quantity={quantity} />
          </ContentRow>
          {reviewCount && isReviewCount && (
            <ReviewCount className="review-count">
              <ReviewIcon className="review-icon">
                <ReviewBubble />
              </ReviewIcon>
              후기
              <ReviewNumber className="review-number">{reviewCount}</ReviewNumber>
            </ReviewCount>
          )}
        </ProductInfo>
      </ProductWrap>
    </Link>
  );
}
